'use client'
import { CMSLink } from '@/components/Link'
import { Media } from '@/components/Media'
import RichText from '@/components/RichText'
import { ChevronsRight } from 'lucide-react'
import { useState } from 'react'

export const AboutBlock = (props: any) => {
  const { icons, eyebrow, title, images, links } = props
  const safeTitle = title || ''
  const [index, setIndex] = useState(0)

  const handleClick = (i: number) => setIndex(i)

  return (
    <section
      className="container mx-auto pt-5"
      aria-labelledby="aboutblock-title"
    >
      <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
        {/* Image Section */}
        <figure className="w-full lg:w-1/2 rounded-3xl overflow-hidden">
          {/* Using optimized Media component for better Core Web Vitals */}
          {images?.[0]?.image && (
            <Media
              resource={images[0].image}
              alt={images?.[0]?.alt || safeTitle || 'About'}
              imgClassName="w-full h-80 sm:h-[28rem] md:h-[30rem] object-cover rounded-3xl"
              priority={false}
              quality={60}
              sizes="(max-width: 640px) 400px, (max-width: 768px) 450px, (max-width: 1024px) 500px, 617px"
              loading="lazy"
            />
          )}
        </figure>

        {/* Content Section */}
        <section className="w-full lg:flex-1 space-y-10">
          {/* Header */}
          <header className="text-left space-y-5">
            {eyebrow && (
              <div className="border border-primary px-4 py-1 text-xs font-medium rounded-full w-fit">
                <span className="text-primary">{eyebrow}</span>
              </div>
            )}
            <div
              id="aboutblock-title"
              className="text-left text-2xl md:text-3xl lg:text-4xl font-bold lg:max-w-3xl text-foreground"
            >
              <RichText data={safeTitle} enableGutter={false} enableProse={false} />
            </div>
          </header>

          {/* Icons Grid */}
          {icons?.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" role="list">
              {icons.map((icon: any, i: number) => (
                <li key={icon?.id || i}>
                  <button
                    type="button"
                    aria-current={index === i ? 'true' : 'false'}
                    tabIndex={0}
                    className={`group border rounded-2xl px-5 py-4 w-full transition-all duration-200 hover:border-primary text-center ${
                      index === i ? 'border-primary bg-muted/30' : 'border-border bg-card'
                    }`}
                    onClick={() => handleClick(i)}
                  >
                    <div className="flex items-center gap-3 justify-center  text-left">
                      {icon?.icon && icon.icon.url && (
                        <Media
                          resource={icon.icon}
                          alt={icon?.label || 'Icon'}
                          className="w-10 h-10 object-contain"
                          priority={false}
                          quality={75}
                          sizes="40px"
                          loading="lazy"
                        />
                      )}
                      {icon?.label && (
                        <div className="font-semibold text-sm text-muted-foreground leading-relaxed">
                          <RichText data={icon.label} enableGutter={false} enableProse={false} />
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Dynamic Content */}
          {icons?.[index]?.content && (
            <div className="text-base leading-relaxed text-muted-foreground">
              <RichText data={icons[index].content} enableGutter={false} enableProse={false} />
            </div>
          )}

          {/* Action Links */}
          {links?.length > 0 && (
            <nav aria-label="About actions" className="pt-2">
              <ul className="flex flex-wrap gap-4" role="list">
                {links.map((link: any, i: number) => (
                  <li key={i}>
                    <CMSLink
                      {...link.link}
                      className="inline-flex items-center gap-3 border border-primary bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base px-6 py-3 rounded-full transition-colors"
                      aria-label={link.label || 'Learn more about this service'}
                    >
                      {link.label}
                      <span className="w-9 h-9 flex items-center justify-center bg-primary text-primary-foreground rounded-full">
                        <ChevronsRight size={18} />
                      </span>
                    </CMSLink>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </section>
      </div>
    </section>
  )
}
